import { useEffect, useState } from 'react';
import { PaymentMethodTypeEnum, SetupFutureUsage } from '@frontend/api-weave-pay';
import { useTranslation } from '@frontend/i18n';
import { CreditCardIcon, Stepper, useAlert } from '@frontend/design-system';
import { useStripePortalPayment } from '../../hooks';
import { StripePaymentMethods } from '../../stripe-components';
import { useStripeStepper } from '../stripe-stepper-context';

interface StripePaymentMethodStepProps {
  allowSavingACHOnFile: boolean;
  allowSavingCardOnFile: boolean;
  showExpressPaymentElement: boolean;
  onSubmit: () => void;
}

const minACHAmountDollars = 25000;
const maxACHAmountDollars = 10000000;

export const StripePaymentMethodStep = ({
  allowSavingACHOnFile,
  allowSavingCardOnFile,
  showExpressPaymentElement,
  onSubmit,
}: StripePaymentMethodStepProps) => {
  const { t } = useTranslation('weave-pay');
  const alerts = useAlert();
  const [paymentElementComplete, setPaymentElementComplete] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodTypeEnum | undefined>(undefined);
  const [saveOnFile, setSaveOnFile] = useState(false);
  const { paymentRequest, setPaymentRequest } = useStripeStepper();
  const { paymentSuccessfullyCompleted } = useStripePortalPayment();

  const handleACHPaymentMethod = () => {
    if (paymentRequest?.amount && paymentRequest?.amount < minACHAmountDollars) {
      alerts.error({
        autoDismissAfter: 8000,
        message: 'Payment amount should not be less than $250 for ACH.',
      });
    } else if (paymentRequest?.amount && paymentRequest?.amount > maxACHAmountDollars) {
      alerts.error({
        autoDismissAfter: 8000,
        message: 'Payment amount should not be more than $100,000 for ACH.',
      });
    }
  };

  const onPaymentChange = (type: string, isComplete: boolean) => {
    switch (type) {
      case 'link':
        setPaymentMethod(PaymentMethodTypeEnum.LINK);
        setSaveOnFile(false);
        break;
      case 'us_bank_account':
        setPaymentMethod(PaymentMethodTypeEnum.ACH);
        handleACHPaymentMethod();
        break;
      case 'affirm':
        setPaymentMethod(PaymentMethodTypeEnum.AFFIRM);
        setSaveOnFile(false);
        break;
      default:
        setPaymentMethod(PaymentMethodTypeEnum.CARD);
        break;
    }
    setPaymentElementComplete(isComplete);
  };

  useEffect(() => {
    setPaymentRequest({
      ...paymentRequest,
      paymentMethodType: paymentMethod,
      setupFutureUsage: saveOnFile ? SetupFutureUsage.offSession : undefined,
    });
  }, [paymentElementComplete]);

  const onSaveCardOnFileChange = (value: boolean) => {
    setSaveOnFile(value);
  };

  const isACHPaymentValid =
    paymentRequest?.paymentMethodType === PaymentMethodTypeEnum.ACH &&
    !!paymentRequest?.amount &&
    (paymentRequest.amount > minACHAmountDollars || paymentRequest.amount < maxACHAmountDollars);

  const isPaymentMethodStepValid = isACHPaymentValid || (paymentElementComplete && !paymentSuccessfullyCompleted);

  const isACH = paymentMethod === PaymentMethodTypeEnum.ACH;

  const showSavePaymentMethod = paymentMethod === PaymentMethodTypeEnum.CARD && allowSavingCardOnFile;

  const handleOnClick = () => {
    setPaymentRequest({
      ...paymentRequest,
      paymentMethodType: paymentMethod,
      setupFutureUsage: saveOnFile ? SetupFutureUsage.offSession : undefined,
    });
  };

  return (
    <>
      <Stepper.Title icon={<CreditCardIcon />}>{t('Enter Credit Card Details')}</Stepper.Title>
      <Stepper.Content>
        <StripePaymentMethods
          isACH={isACH}
          onSubmit={onSubmit}
          onPaymentChange={onPaymentChange}
          onSaveChange={onSaveCardOnFileChange}
          shouldShowExpressElement={showExpressPaymentElement}
          shouldShowSavePaymentMethodOnFile={isACH ? allowSavingACHOnFile : showSavePaymentMethod}
        />
      </Stepper.Content>
      <Stepper.ButtonBar>
        <Stepper.NextButton isValid={isPaymentMethodStepValid} onClick={handleOnClick} />
      </Stepper.ButtonBar>
    </>
  );
};
